import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from "@/components/ui/collapsible";
import { AnimatePresence, motion } from "framer-motion";
import { CaretDown, CaretRight, WarningCircle, PencilSimple, ArrowsClockwise, SpinnerGap, Check, X } from "@phosphor-icons/react";
import { REWRITE_CHIPS } from "./talkingDocConstants";

export function CollapsibleGroup({ label, accent, count, children }) {
  const [open, setOpen] = useState(true);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger asChild>
        <button className="flex items-center gap-2 w-full py-2 group" data-testid={`td-group-${label.replace(/\s+/g, "-").toLowerCase()}`}>
          {open ? <CaretDown size={14} style={{ color: accent }} /> : <CaretRight size={14} style={{ color: accent }} />}
          <h3 className="text-xs font-bold uppercase tracking-wider" style={{ color: accent }}>{label}</h3>
          <span className="text-[10px] text-[var(--text-muted)] bg-[var(--bg-hover)] px-1.5 py-0.5 rounded-full">{count}</span>
          <span className="text-[10px] text-[var(--text-muted)] ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
            {open ? "Collapse" : "Expand"}
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3 pb-2">
          {children}
        </motion.div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SectionBlock({ label, content, isEditing, editText, onStartEdit, onCancelEdit, onSaveEdit, onEditTextChange,
  onRegenerate, isRegenerating, onRewriteChip, rewritingChip, hasViolation, violationPhrases }) {

  const [showChips, setShowChips] = useState(false);

  return (
    <div className={`bg-[var(--bg-card)] border rounded-xl p-4 transition-all ${hasViolation ? "border-red-500/50" : "border-[var(--border)]"}`} data-testid={`td-section-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-sm font-semibold text-[var(--text-primary)] truncate">{label}</h4>
          {hasViolation && (
            <span className="flex items-center gap-1 text-[10px] text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full shrink-0">
              <WarningCircle size={10} /> Banned: {violationPhrases.join(", ")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isEditing && (
            <>
              <button onClick={onStartEdit} className="p-1.5 rounded text-[var(--text-muted)] hover:text-[#FF4400] hover:bg-[var(--bg-hover)] transition-colors" title="Edit"><PencilSimple size={14} /></button>
              <button onClick={onRegenerate} disabled={isRegenerating} className="p-1.5 rounded text-[var(--text-muted)] hover:text-[#3B82F6] hover:bg-[var(--bg-hover)] transition-colors" title="Regenerate">
                {isRegenerating ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
              </button>
              <button onClick={() => setShowChips(!showChips)} className={`p-1.5 rounded transition-colors ${showChips ? "text-[#FF4400] bg-[#FF4400]/10" : "text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)]"}`} title="Rewrite options">
                <CaretDown size={14} className={`transition-transform ${showChips ? "rotate-180" : ""}`} />
              </button>
            </>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-2">
          <Textarea value={editText} onChange={e => onEditTextChange(e.target.value)} rows={8} className="bg-[var(--bg-base)] border-[var(--border-strong)] text-sm font-mono resize-y" />
          <div className="flex gap-2">
            <Button onClick={onSaveEdit} className="text-xs h-10 sm:h-7 bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 gap-1"><Check size={12} />Save</Button>
            <Button onClick={onCancelEdit} className="text-xs h-10 sm:h-7 bg-[var(--bg-base)] text-[var(--text-muted)] hover:bg-[var(--bg-hover)] border border-[var(--border)] gap-1"><X size={12} />Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-[var(--text-secondary)] leading-relaxed whitespace-pre-wrap">{content}</div>
      )}

      <AnimatePresence>
        {showChips && !isEditing && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-[var(--border)]">
              {REWRITE_CHIPS.map(chip => (
                <button key={chip.key} onClick={() => onRewriteChip(chip.key)}
                  disabled={rewritingChip === chip.key}
                  className={`px-2.5 py-1 rounded-full text-[10px] font-medium border transition-all ${rewritingChip === chip.key
                    ? "bg-[#FF4400]/20 border-[#FF4400]/40 text-[#FF4400] animate-pulse"
                    : "bg-[var(--bg-base)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[#FF4400]/40 hover:text-[#FF4400]"
                  }`}>
                  {rewritingChip === chip.key ? "Rewriting..." : chip.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
